import { Database, TablesInsert, TablesUpdate } from '@/supabase/types'
import { SupabaseClient } from '@supabase/supabase-js'

import { applyBusinessSeatEntitlement } from '@/db/losi-business-seats'
import { ensureUserBootstrap } from '@/lib/auth/user-bootstrap'
import { rewriteSupabaseStorageUrl } from '@/lib/media/safe-url'
import { createServiceClient } from '@/lib/supabase/service-client'
import { isDev } from '@/lib/utils/isDev'
import { safeConsole } from '@/lib/utils/safeConsole'

const PROFILE_CACHE_TTL_MS = 3000
const profileInflightByUserId = new Map<string, Promise<Database['public']['Tables']['profiles']['Row'] | null>>()
const profileCacheByUserId = new Map<
  string,
  {
    expiresAt: number
    profile: Database['public']['Tables']['profiles']['Row'] | null
  }
>()

// --------------

function invalidateProfileCache(userId: string | null | undefined) {
  if (!userId) return

  profileCacheByUserId.delete(userId)
  profileInflightByUserId.delete(userId)
}

/** Use service client on server when no client passed; browser client only on client. */
function getDefaultClient(passed?: SupabaseClient<Database>): SupabaseClient<Database> {
  if (passed) return passed
  if (typeof window === 'undefined') return createServiceClient()
  return require('@/lib/supabase/browser-client').supabase as SupabaseClient<Database>
}

// --------------

function shouldRepairProfile(profile: Database['public']['Tables']['profiles']['Row'] | null) {
  return Boolean(profile && (!profile.email || !profile.email.trim()))
}

// --------------

function normalizeProfileStorageUrls(
  profile: Database['public']['Tables']['profiles']['Row']
): Database['public']['Tables']['profiles']['Row'] {
  if (!profile.image_url || typeof profile.image_url !== 'string') {
    return profile
  }

  const rewritten = rewriteSupabaseStorageUrl(profile.image_url)
  if (rewritten === profile.image_url) {
    return profile
  }

  return { ...profile, image_url: rewritten }
}

// --------------

async function fetchOwnProfileViaApi(userId: string) {
  const response = await fetch('/api/v1/profile', {
    cache: 'no-store',
    credentials: 'include'
  }).catch(() => null)

  if (!response?.ok) {
    return null
  }

  const profile = await response.json().catch(() => null)

  if (!profile || profile.user_id !== userId) {
    return null
  }

  return profile as Database['public']['Tables']['profiles']['Row']
}

export const getProfileByUserId = async (userId: string, supabaseClient?: SupabaseClient) => {
  const isBrowser = typeof window !== 'undefined'
  const shouldDeduplicate = isBrowser && !supabaseClient

  if (shouldDeduplicate) {
    const cached = profileCacheByUserId.get(userId)
    if (cached && cached.expiresAt > Date.now()) {
      return cached.profile
    }

    const inflight = profileInflightByUserId.get(userId)
    if (inflight) {
      return inflight
    }
  }

  const client = getDefaultClient(supabaseClient) as SupabaseClient<Database>
  const fetchProfile = async () => {
    const { data: profile, error } = await client
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      safeConsole.error(`Error fetching profile for user ${userId}:`, error)
      throw new Error(error.message || 'Failed to fetch profile')
    }

    if (!profile) {
      if (!isBrowser) {
        const repaired = await ensureUserBootstrap(userId)
        return repaired.profile
      }

      if (!supabaseClient) {
        const repairedProfile = await fetchOwnProfileViaApi(userId)
        if (repairedProfile) {
          return repairedProfile
        }
      }

      safeConsole.warn(`No profile found for user ${userId}`)
      return null
    }

    let effectiveProfile = profile

    if (shouldRepairProfile(profile)) {
      if (!isBrowser) {
        const repaired = await ensureUserBootstrap(userId)
        effectiveProfile = repaired.profile
      } else if (!supabaseClient) {
        effectiveProfile = (await fetchOwnProfileViaApi(userId)) ?? profile
      }
    }

    // Ensure the profile has enable_api_access property (for backward compatibility)
    if (
      effectiveProfile &&
      !Object.prototype.hasOwnProperty.call(effectiveProfile, 'enable_api_access')
    ) {
      safeConsole.log(`Adding missing enable_api_access property to profile for user ${userId}`)
      // @ts-ignore - We're deliberately adding a property that might not be in the type
      effectiveProfile.enable_api_access = false
    }

    if (
      effectiveProfile &&
      !Object.prototype.hasOwnProperty.call(effectiveProfile, 'experimental_sidecar_agents')
    ) {
      safeConsole.log(
        `Adding missing experimental_sidecar_agents property to profile for user ${userId}`
      )
      // @ts-ignore - We're deliberately adding a property that might not be in the type
      effectiveProfile.experimental_sidecar_agents = true
    }

    const entitledProfile = effectiveProfile
      ? await applyBusinessSeatEntitlement(normalizeProfileStorageUrls(effectiveProfile))
      : effectiveProfile

    // Persist rewritten storage hosts so next/image and raw <img> stop hitting dead origins.
    if (
      entitledProfile &&
      typeof profile.image_url === 'string' &&
      entitledProfile.image_url !== profile.image_url
    ) {
      void client
        .from('profiles')
        .update({ image_url: entitledProfile.image_url })
        .eq('id', entitledProfile.id)
        .then(({ error: persistError }) => {
          if (persistError) {
            safeConsole.warn('Failed to persist rewritten profile image_url:', persistError.message)
          }
        })
    }

    if (shouldDeduplicate) {
      profileCacheByUserId.set(userId, {
        profile: entitledProfile,
        expiresAt: Date.now() + PROFILE_CACHE_TTL_MS
      })
    }

    return entitledProfile
  }

  if (!shouldDeduplicate) {
    return fetchProfile()
  }

  const fetchPromise = fetchProfile()
  profileInflightByUserId.set(userId, fetchPromise)

  try {
    return await fetchPromise
  } finally {
    profileInflightByUserId.delete(userId)
  }
}

export const getProfilesByUserId = async (userId: string) => {
  const client = getDefaultClient() as SupabaseClient<Database>
  const { data: profiles, error } = await client
    .from('profiles')
    .select('*')
    .eq('user_id', userId)

  if (!profiles) {
    throw new Error(error.message)
  }

  return profiles
}

export const createProfile = async (profile: TablesInsert<'profiles'>) => {
  const client = getDefaultClient() as SupabaseClient<Database>
  const { data: createdProfile, error } = await client
    .from('profiles')
    .insert([profile])
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  return createdProfile
}

export const updateProfile = async (profileId: string, profile: TablesUpdate<'profiles'>) => {
  const client = getDefaultClient() as SupabaseClient<Database>
  const { data: updatedProfile, error } = await client
    .from('profiles')
    .update(profile)
    .eq('id', profileId)
    .select('*')
    .single()

  if (error) {
    throw new Error(error.message)
  }

  invalidateProfileCache(updatedProfile.user_id)

  return updatedProfile
}

export const deleteProfile = async (profileId: string) => {
  const client = getDefaultClient() as SupabaseClient<Database>
  const { error } = await client.from('profiles').delete().eq('id', profileId)

  if (error) {
    throw new Error(error.message)
  }

  return true
}

export async function updateProfileByStripeCustomerId(
  supabaseAdmin: SupabaseClient,
  stripeCustomerId: string,
  profile: Database['public']['Tables']['profiles']['Update']
) {
  return supabaseAdmin
    .from('profiles')
    .update(profile)
    .eq('stripe_customer_id', stripeCustomerId)
    .select('*')
    .single()
}

export async function getProfileByStripeCustomerId(
  supabaseAdmin: SupabaseClient,
  stripeCustomerId: string
) {
  return supabaseAdmin
    .from('profiles')
    .select('*')
    .eq('stripe_customer_id', stripeCustomerId)
    .single()
}

export function updateProfileByUserId(
  supabaseAdmin: SupabaseClient,
  userId: string,
  profile: Database['public']['Tables']['profiles']['Update']
) {
  return supabaseAdmin.from('profiles').update(profile).eq('user_id', userId).select('*').single()
}

/**
 * Ensures the enable_api_access column exists in the profiles table
 * This is helpful during database migrations or when running in environments
 * where the column might not exist yet.
 */
export const ensureApiAccessColumn = async (supabaseClient?: SupabaseClient) => {
  try {
    safeConsole.log('Checking for enable_api_access column in profiles table...')
    const client = getDefaultClient(supabaseClient) as SupabaseClient<Database>
    const rpcClient = client as unknown as { rpc: (name: string, args?: Record<string, string>) => Promise<{ data: unknown; error: unknown }> }

    // Try to execute a query that checks if the column exists
    // This uses PostgreSQL's information_schema to check column existence
    const { data, error } = await rpcClient.rpc('check_column_exists', {
      table_name: 'profiles',
      column_name: 'enable_api_access'
    })

    if (error) {
      safeConsole.error('Error checking for column:', error)

      // If the function doesn't exist, we'll create it
      await (client as unknown as { rpc: (name: string) => Promise<unknown> }).rpc('create_check_column_function')

      // Try the check again
      const { data: retryData, error: retryError } = await rpcClient.rpc('check_column_exists', {
        table_name: 'profiles',
        column_name: 'enable_api_access'
      })

      if (retryError) {
        safeConsole.error('Failed to check column existence:', retryError)
        return false
      }

      if (!retryData) {
        // Column doesn't exist, try to add it
        safeConsole.log('Adding enable_api_access column to profiles table...')
        await client
          .from('profiles')
          .select('*')
          .limit(0)
          .then(() => {
            return (client as unknown as { rpc: (name: string, args: { sql_query: string }) => Promise<unknown> }).rpc('execute_sql', {
              sql_query:
                'ALTER TABLE profiles ADD COLUMN IF NOT EXISTS enable_api_access BOOLEAN DEFAULT FALSE;'
            })
          })
        safeConsole.log('Column added successfully')
        return true
      }
    }

    if (!data) {
      // Column doesn't exist, try to add it
      safeConsole.log('Adding enable_api_access column to profiles table...')
      await client
        .from('profiles')
        .select('*')
        .limit(0)
        .then(() => {
          return (client as unknown as { rpc: (name: string, args: { sql_query: string }) => Promise<unknown> }).rpc('execute_sql', {
            sql_query:
              'ALTER TABLE profiles ADD COLUMN IF NOT EXISTS enable_api_access BOOLEAN DEFAULT FALSE;'
          })
        })
      safeConsole.log('Column added successfully')
      return true
    }

    safeConsole.log('enable_api_access column already exists')
    return true
  } catch (error) {
    safeConsole.error('Error ensuring API access column:', error)
    return false
  }
}
